import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft,
  ArrowRight,
  User,
  Building2,
  Home,
  FileText,
  Check,
  MapPin,
  Ruler,
  Shield,
  Flag,
  Phone,
  Mail,
  Users,
  Building,
  Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

// Profils pour MPR (tous)
const PROFILE_CONFIG_MPR = {
  bailleur: { 
    label: "Bailleur", 
    icon: Building2, 
    description: "Propriétaire louant un bien immobilier"
  },
  proprietaire_occupant: { 
    label: "Propriétaire Occupant", 
    icon: Home, 
    description: "Propriétaire résidant dans son bien"
  },
  sci: { 
    label: "SCI", 
    icon: FileText, 
    description: "Société Civile Immobilière"
  }
};

// Profils pour 174 (sans Propriétaire Occupant)
const PROFILE_CONFIG_174 = {
  bailleur: { 
    label: "Bailleur", 
    icon: Building2, 
    description: "Propriétaire louant un bien immobilier"
  },
  sci: { 
    label: "SCI", 
    icon: FileText, 
    description: "Société Civile Immobilière"
  }
};

// CEE 174 par nombre de classes (du simulateur)
const CEE_174 = {
  2: 5784,
  3: 7136,
  4: 9104
};

// Coefficients par surface pour 174
const COEFFICIENTS_174 = [
  { min: 0, max: 34, coef: 0.40 },
  { min: 35, max: 59, coef: 0.50 },
  { min: 60, max: 89, coef: 0.80 },
  { min: 90, max: 109, coef: 1.00 },
  { min: 110, max: 129, coef: 1.20 },
  { min: 130, max: 9999, coef: 1.30 }
];

const getCoefficient174 = (surface) => {
  if (!surface) return 1.0;
  for (const range of COEFFICIENTS_174) {
    if (surface >= range.min && surface <= range.max) {
      return range.coef;
    }
  }
  return 1.30;
};

const calculerMontantDevis174 = (nombreLogements, surfaces, scenarioClasses = 2) => {
  const ceePar = CEE_174[scenarioClasses] || CEE_174[2];
  let total = 0;
  
  for (let i = 0; i < nombreLogements; i++) {
    const surface = surfaces[i] || 70;
    const coef = getCoefficient174(surface);
    total += ceePar * coef;
  }
  
  return Math.round(total);
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export default function DossierCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get initial tab from URL
  const urlParams = new URLSearchParams(location.search);
  const initialTab = urlParams.get('tab') || 'mpr';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // MPR form data
  const [mprFormData, setMprFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    profile: "",
    programme: "",
    zone: "",
    metrage: "",
    precarite: "",
    code_postal: "",
    notes: ""
  });
  
  // 174 form data
  const [form174Data, setForm174Data] = useState({
    profile: "",
    zone: "",
    organisme_bailleur: "",
    nom_representant: "",
    tel_representant: "",
    fixe_representant: "",
    email_representant: "",
    adresse_bailleur: "",
    nom_residence: "",
    adresse_residence: "",
    code_postal: "",
    coordonnees_gardien: "",
    nombre_logements: 1,
    type_logements: "appartement",
    scenario_classes: 2,
    notes: ""
  });
  
  // Gestion des surfaces par logement pour 174
  const [useCustomSurfaces, setUseCustomSurfaces] = useState(false);
  const [defaultSurface, setDefaultSurface] = useState(70);
  const [customSurfaces, setCustomSurfaces] = useState([70]);
  
  // Montant devis calculé pour 174
  const [montantDevis174, setMontantDevis174] = useState(0);

  const isBleuJaune = ["BLEU", "JAUNE"].includes(mprFormData.precarite);

  useEffect(() => {
    fetchProfiles();
  }, []);

  // Recalculer le montant devis 174 quand les paramètres changent
  useEffect(() => {
    const surfaces = useCustomSurfaces 
      ? customSurfaces 
      : Array(form174Data.nombre_logements).fill(defaultSurface);
    const montant = calculerMontantDevis174(form174Data.nombre_logements, surfaces, form174Data.scenario_classes);
    setMontantDevis174(montant);
  }, [form174Data.nombre_logements, form174Data.scenario_classes, useCustomSurfaces, defaultSurface, customSurfaces]);

  // Ajuster le nombre de surfaces personnalisées quand le nombre de logements change
  useEffect(() => {
    if (useCustomSurfaces) {
      const currentLength = customSurfaces.length;
      const targetLength = form174Data.nombre_logements;
      
      if (targetLength > currentLength) {
        setCustomSurfaces([...customSurfaces, ...Array(targetLength - currentLength).fill(defaultSurface)]);
      } else if (targetLength < currentLength) {
        setCustomSurfaces(customSurfaces.slice(0, targetLength));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form174Data.nombre_logements, useCustomSurfaces]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`);
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/dossiers/new?tab=${tab}`, { replace: true });
  };

  const handleMprSubmit = async (e) => {
    e.preventDefault();
    
    if (!mprFormData.client_name || !mprFormData.profile) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...mprFormData,
        metrage: mprFormData.metrage ? parseFloat(mprFormData.metrage) : null,
        programme: mprFormData.programme || null,
        zone: mprFormData.zone || null,
        precarite: mprFormData.precarite || null
      };
      
      const response = await axios.post(`${API}/dossiers`, payload);
      toast.success("Dossier créé avec succès");
      navigate(`/dossiers/${response.data.id}`);
    } catch (error) {
      console.error("Error creating dossier:", error);
      toast.error("Erreur lors de la création du dossier");
    } finally {
      setLoading(false);
    }
  };

  const handle174Submit = async (e) => {
    e.preventDefault();
    
    if (!form174Data.organisme_bailleur || !form174Data.profile) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const surfaces = useCustomSurfaces 
        ? customSurfaces 
        : Array(form174Data.nombre_logements).fill(defaultSurface);
      
      const payload = {
        client_name: form174Data.organisme_bailleur,
        profile: form174Data.profile,
        programme: "174",
        zone: form174Data.zone || null,
        organisme_bailleur: form174Data.organisme_bailleur,
        nom_representant: form174Data.nom_representant,
        tel_representant: form174Data.tel_representant,
        fixe_representant: form174Data.fixe_representant,
        email_representant: form174Data.email_representant,
        adresse_bailleur: form174Data.adresse_bailleur,
        nom_residence: form174Data.nom_residence,
        adresse_residence: form174Data.adresse_residence,
        coordonnees_gardien: form174Data.coordonnees_gardien,
        nombre_logements: form174Data.nombre_logements,
        type_logements: form174Data.type_logements,
        surfaces_logements: surfaces,
        montant_devis_174: montantDevis174,
        scenario_classes: form174Data.scenario_classes,
        notes: form174Data.notes
      };
      
      const response = await axios.post(`${API}/dossiers`, payload);
      toast.success("Dossier 174 créé avec succès");
      navigate(`/dossiers/${response.data.id}`);
    } catch (error) {
      console.error("Error creating dossier:", error);
      toast.error("Erreur lors de la création du dossier");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSurfaceChange = (index, value) => {
    const newSurfaces = [...customSurfaces];
    newSurfaces[index] = parseFloat(value) || 0;
    setCustomSurfaces(newSurfaces);
  };

  const selectedMprProfile = profiles.find(p => p.key === mprFormData.profile);

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(`/dossiers?tab=${activeTab}`)}
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground rounded-xl"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
          Nouveau dossier
        </h1>
        <p className="text-muted-foreground mt-1">
          Créez un nouveau dossier client en remplissant les informations ci-dessous
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">
        <TabsList className="bg-card rounded-xl p-1.5 shadow-lg border border-border">
          <TabsTrigger 
            value="mpr" 
            className="rounded-xl data-[state=active]:bg-[hsl(145,100%,45%)]/10 data-[state=active]:text-[hsl(145,100%,39%)] px-8 py-2.5 font-semibold transition-all"
            data-testid="tab-mpr"
          >
            MPR
          </TabsTrigger>
          <TabsTrigger 
            value="174" 
            className="rounded-xl data-[state=active]:bg-[hsl(36,100%,50%)]/10 data-[state=active]:text-[hsl(36,100%,45%)] px-8 py-2.5 font-semibold transition-all"
            data-testid="tab-174"
          >
            174
          </TabsTrigger>
          <TabsTrigger 
            value="cumule" 
            className="rounded-xl data-[state=active]:bg-violet-500/10 data-[state=active]:text-violet-600 px-8 py-2.5 font-semibold transition-all"
            data-testid="tab-cumule"
          >
            Op. Cumulée
          </TabsTrigger>
        </TabsList>

        {/* MPR Form */}
        <TabsContent value="mpr">
          <form onSubmit={handleMprSubmit} className="max-w-4xl space-y-8">
            {/* Profile Selection */}
            <div className="space-y-4">
              <Label className="text-base font-heading font-semibold">
                Profil client <span className="text-destructive">*</span>
              </Label>
              <RadioGroup
                value={mprFormData.profile}
                onValueChange={(value) => setMprFormData({ ...mprFormData, profile: value })}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {Object.entries(PROFILE_CONFIG_MPR).map(([key, config]) => {
                  const Icon = config.icon;
                  const isSelected = mprFormData.profile === key;
                  
                  return (
                    <Label
                      key={key}
                      htmlFor={`mpr-${key}`}
                      className={cn(
                        "flex flex-col items-center p-6 border rounded-xl cursor-pointer transition-all shadow-lg hover:shadow-xl",
                        isSelected 
                          ? "border-[hsl(145,100%,45%)] bg-[hsl(145,100%,45%)]/5" 
                          : "border-border hover:border-[hsl(145,100%,45%)]/50 bg-card"
                      )}
                      data-testid={`profile-${key}`}
                    >
                      <RadioGroupItem value={key} id={`mpr-${key}`} className="sr-only" />
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center mb-3",
                        isSelected ? "bg-[hsl(145,100%,45%)] text-white" : "bg-muted"
                      )}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="font-medium text-center">{config.label}</span>
                      <span className="text-xs text-muted-foreground text-center mt-1">
                        {config.description}
                      </span>
                      {isSelected && (
                        <div className="mt-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </Label>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Required Documents Preview */}
            {selectedMprProfile && (
              <div className="border border-border rounded-sm p-4 bg-muted/30">
                <p className="text-sm font-medium mb-3">
                  Documents requis pour ce profil ({selectedMprProfile.documents.length}) :
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedMprProfile.documents.map((doc) => (
                    <span 
                      key={doc.key}
                      className="inline-flex items-center px-2.5 py-1 text-xs bg-card border border-border rounded-sm"
                    >
                      <FileText className="w-3 h-3 mr-1.5 text-muted-foreground" />
                      {doc.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Programme & Zone */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Flag className="w-5 h-5 text-primary" />
                Programme
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Programme</Label>
                  <Select 
                    value={mprFormData.programme} 
                    onValueChange={(value) => setMprFormData({ ...mprFormData, programme: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="programme-select">
                      <SelectValue placeholder="Sélectionner un programme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAC">PAC</SelectItem>
                      <SelectItem value="PAC/SSC">PAC/SSC</SelectItem>
                      <SelectItem value="PAC + CESI">PAC + CESI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Zone climatique
                  </Label>
                  <Select 
                    value={mprFormData.zone} 
                    onValueChange={(value) => setMprFormData({ ...mprFormData, zone: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="zone-select">
                      <SelectValue placeholder="Sélectionner une zone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="H1">H1 - Nord et Est</SelectItem>
                      <SelectItem value="H2">H2 - Ouest et Centre</SelectItem>
                      <SelectItem value="H3">H3 - Sud et Méditerranée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Informations client
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label htmlFor="client_name">
                    Nom du client <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="client_name"
                    data-testid="client-name-input"
                    value={mprFormData.client_name}
                    onChange={(e) => setMprFormData({ ...mprFormData, client_name: e.target.value })}
                    placeholder="Ex: Jean Dupont"
                    className="rounded-sm"
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="client_email">Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    data-testid="client-email-input"
                    value={mprFormData.client_email}
                    onChange={(e) => setMprFormData({ ...mprFormData, client_email: e.target.value })}
                    placeholder="Ex: jean.dupont@email.com"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="client_phone">Téléphone</Label>
                  <Input
                    id="client_phone"
                    data-testid="client-phone-input"
                    value={mprFormData.client_phone}
                    onChange={(e) => setMprFormData({ ...mprFormData, client_phone: e.target.value })}
                    placeholder="Ex: 06 12 34 56 78"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="code_postal">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Code postal
                  </Label>
                  <Input
                    id="code_postal"
                    data-testid="code-postal-input"
                    value={mprFormData.code_postal}
                    onChange={(e) => setMprFormData({ ...mprFormData, code_postal: e.target.value })}
                    placeholder="Ex: 75001"
                    className="rounded-sm"
                    maxLength={5}
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="metrage">
                    <Ruler className="w-4 h-4 inline mr-1" />
                    Métrage (m²)
                  </Label>
                  <Input
                    id="metrage"
                    type="number"
                    data-testid="metrage-input"
                    value={mprFormData.metrage}
                    onChange={(e) => setMprFormData({ ...mprFormData, metrage: e.target.value })}
                    placeholder="Ex: 85"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label>
                    <Shield className="w-4 h-4 inline mr-1" />
                    Précarité énergétique
                  </Label>
                  <Select 
                    value={mprFormData.precarite} 
                    onValueChange={(value) => setMprFormData({ ...mprFormData, precarite: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="precarite-select">
                      <SelectValue placeholder="Sélectionner un niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BLEU">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          Bleu - Modeste
                        </div>
                      </SelectItem>
                      <SelectItem value="JAUNE">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-yellow-500/100"></div>
                          Jaune - Intermédiaire
                        </div>
                      </SelectItem>
                      <SelectItem value="VIOLET">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-purple-500/100"></div>
                          Violet - Supérieur
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Info sur le devis auto-calculé */}
              {isBleuJaune && (
                <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <p className="text-sm text-blue-800">
                    <strong>Note :</strong> Pour les clients {mprFormData.precarite === "BLEU" ? "Bleu" : "Jaune"}, 
                    le montant du devis sera automatiquement calculé (= MPR + CEE) lors de la création du dossier.
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="form-group">
              <Label htmlFor="mpr_notes">Notes (optionnel)</Label>
              <Textarea
                id="mpr_notes"
                data-testid="notes-input"
                value={mprFormData.notes}
                onChange={(e) => setMprFormData({ ...mprFormData, notes: e.target.value })}
                placeholder="Informations complémentaires sur le dossier..."
                className="rounded-sm min-h-[100px]"
              />
            </div>

            {/* Submit */}
            <div className="flex items-center gap-4 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/dossiers?tab=mpr`)}
                className="rounded-sm"
                data-testid="cancel-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading || !mprFormData.client_name || !mprFormData.profile}
                className="rounded-sm shadow-none"
                data-testid="submit-btn"
              >
                {loading ? "Création..." : "Créer le dossier"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* 174 Form */}
        <TabsContent value="174">
          <form onSubmit={handle174Submit} className="max-w-4xl space-y-8">
            {/* Profile Selection (sans Propriétaire Occupant) */}
            <div className="space-y-4">
              <Label className="text-base font-heading font-semibold">
                Profil client <span className="text-destructive">*</span>
              </Label>
              <RadioGroup
                value={form174Data.profile}
                onValueChange={(value) => setForm174Data({ ...form174Data, profile: value })}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                {Object.entries(PROFILE_CONFIG_174).map(([key, config]) => {
                  const Icon = config.icon;
                  const isSelected = form174Data.profile === key;
                  
                  return (
                    <Label
                      key={key}
                      htmlFor={`174-${key}`}
                      className={cn(
                        "flex flex-col items-center p-6 border rounded-sm cursor-pointer transition-all",
                        isSelected 
                          ? "border-[hsl(145,100%,45%)] bg-muted" 
                          : "border-border hover:border-[hsl(145,100%,45%)]/50"
                      )}
                      data-testid={`profile-174-${key}`}
                    >
                      <RadioGroupItem value={key} id={`174-${key}`} className="sr-only" />
                      <div className={cn(
                        "w-12 h-12 rounded-sm flex items-center justify-center mb-3",
                        isSelected ? "bg-[hsl(145,100%,45%)] text-white" : "bg-muted"
                      )}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="font-medium text-center">{config.label}</span>
                      <span className="text-xs text-muted-foreground text-center mt-1">
                        {config.description}
                      </span>
                      {isSelected && (
                        <div className="mt-3 w-5 h-5 bg-[hsl(145,100%,45%)] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </Label>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Zone climatique */}
            <div className="form-group max-w-xs">
              <Label>
                <MapPin className="w-4 h-4 inline mr-1" />
                Zone climatique
              </Label>
              <Select 
                value={form174Data.zone} 
                onValueChange={(value) => setForm174Data({ ...form174Data, zone: value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="zone-174-select">
                  <SelectValue placeholder="Sélectionner une zone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="H1">H1 - Nord et Est</SelectItem>
                  <SelectItem value="H2">H2 - Ouest et Centre</SelectItem>
                  <SelectItem value="H3">H3 - Sud et Méditerranée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Section Bailleur */}
            <div className="space-y-4 border border-border rounded-sm p-6 bg-muted/50">
              <h3 className="font-medium text-sm uppercase tracking-wide text-foreground border-b border-border pb-2 flex items-center gap-2">
                <Building className="w-4 h-4" />
                Organisme Bailleur
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label htmlFor="organisme_bailleur">
                    Nom de l'organisme bailleur <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="organisme_bailleur"
                    data-testid="organisme-bailleur-input"
                    value={form174Data.organisme_bailleur}
                    onChange={(e) => setForm174Data({ ...form174Data, organisme_bailleur: e.target.value })}
                    placeholder="Ex: Office HLM de Paris"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="nom_representant">Nom du représentant</Label>
                  <Input
                    id="nom_representant"
                    data-testid="nom-representant-input"
                    value={form174Data.nom_representant}
                    onChange={(e) => setForm174Data({ ...form174Data, nom_representant: e.target.value })}
                    placeholder="Ex: M. Martin"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="tel_representant">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Téléphone portable
                  </Label>
                  <Input
                    id="tel_representant"
                    data-testid="tel-representant-input"
                    value={form174Data.tel_representant}
                    onChange={(e) => setForm174Data({ ...form174Data, tel_representant: e.target.value })}
                    placeholder="Ex: 06 12 34 56 78"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="fixe_representant">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Téléphone fixe
                  </Label>
                  <Input
                    id="fixe_representant"
                    data-testid="fixe-representant-input"
                    value={form174Data.fixe_representant}
                    onChange={(e) => setForm174Data({ ...form174Data, fixe_representant: e.target.value })}
                    placeholder="Ex: 01 23 45 67 89"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email_representant">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email
                  </Label>
                  <Input
                    id="email_representant"
                    type="email"
                    data-testid="email-representant-input"
                    value={form174Data.email_representant}
                    onChange={(e) => setForm174Data({ ...form174Data, email_representant: e.target.value })}
                    placeholder="Ex: contact@bailleur.fr"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="adresse_bailleur">Adresse du bailleur</Label>
                  <Input
                    id="adresse_bailleur"
                    data-testid="adresse-bailleur-input"
                    value={form174Data.adresse_bailleur}
                    onChange={(e) => setForm174Data({ ...form174Data, adresse_bailleur: e.target.value })}
                    placeholder="Ex: 10 rue de la Paix, 75001 Paris"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Section Résidence */}
            <div className="space-y-4 border border-border rounded-sm p-6 bg-muted/50">
              <h3 className="font-medium text-sm uppercase tracking-wide text-foreground border-b border-border pb-2 flex items-center gap-2">
                <Home className="w-4 h-4" />
                Résidence
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label htmlFor="nom_residence">Nom de la résidence</Label>
                  <Input
                    id="nom_residence"
                    data-testid="nom-residence-input"
                    value={form174Data.nom_residence}
                    onChange={(e) => setForm174Data({ ...form174Data, nom_residence: e.target.value })}
                    placeholder="Ex: Résidence Les Lilas"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="adresse_residence">Adresse de la résidence</Label>
                  <Input
                    id="adresse_residence"
                    data-testid="adresse-residence-input"
                    value={form174Data.adresse_residence}
                    onChange={(e) => setForm174Data({ ...form174Data, adresse_residence: e.target.value })}
                    placeholder="Ex: 25 avenue des Fleurs, 93000 Bobigny"
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="code_postal_174">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Code postal
                  </Label>
                  <Input
                    id="code_postal_174"
                    data-testid="code-postal-174-input"
                    value={form174Data.code_postal}
                    onChange={(e) => setForm174Data({ ...form174Data, code_postal: e.target.value })}
                    placeholder="Ex: 93000"
                    className="rounded-sm"
                    maxLength={5}
                  />
                </div>
                <div className="form-group md:col-span-2">
                  <Label htmlFor="coordonnees_gardien">Coordonnées du gardien (optionnel)</Label>
                  <Input
                    id="coordonnees_gardien"
                    data-testid="coordonnees-gardien-input"
                    value={form174Data.coordonnees_gardien}
                    onChange={(e) => setForm174Data({ ...form174Data, coordonnees_gardien: e.target.value })}
                    placeholder="Ex: M. Dupont - 06 98 76 54 32"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Section Logements */}
            <div className="space-y-4 border border-border rounded-sm p-6 bg-muted/50">
              <h3 className="font-medium text-sm uppercase tracking-wide text-foreground border-b border-border pb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Logements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <Label>Type de logements</Label>
                  <Select 
                    value={form174Data.type_logements} 
                    onValueChange={(value) => setForm174Data({ ...form174Data, type_logements: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="type-logements-select">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartement">Appartements (immeuble)</SelectItem>
                      <SelectItem value="maison">Maisons (lotissement)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label htmlFor="nombre_logements">
                    <Hash className="w-4 h-4 inline mr-1" />
                    Nombre de {form174Data.type_logements === "maison" ? "maisons" : "appartements"}
                  </Label>
                  <Input
                    id="nombre_logements"
                    type="number"
                    min="1"
                    data-testid="nombre-logements-input"
                    value={form174Data.nombre_logements}
                    onChange={(e) => setForm174Data({ ...form174Data, nombre_logements: parseInt(e.target.value) || 1 })}
                    className="rounded-sm"
                  />
                </div>
                <div className="form-group">
                  <Label>Scénario (classes)</Label>
                  <Select 
                    value={form174Data.scenario_classes.toString()} 
                    onValueChange={(value) => setForm174Data({ ...form174Data, scenario_classes: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="scenario-classes-select">
                      <SelectValue placeholder="Classes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 classes</SelectItem>
                      <SelectItem value="3">3 classes</SelectItem>
                      <SelectItem value="4">4 classes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Surfaces des logements */}
              <div className="mt-4 p-4 bg-card/60 rounded-sm border border-border">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm font-medium">Surfaces des logements</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setUseCustomSurfaces(!useCustomSurfaces)}
                    className="rounded-sm text-xs"
                    data-testid="toggle-custom-surfaces"
                  >
                    {useCustomSurfaces ? "Utiliser surface par défaut" : "Personnaliser par logement"}
                  </Button>
                </div>

                {!useCustomSurfaces ? (
                  <div className="flex items-center gap-4">
                    <Label htmlFor="default_surface" className="text-sm text-muted-foreground whitespace-nowrap">
                      Surface par défaut :
                    </Label>
                    <Input
                      id="default_surface"
                      type="number"
                      value={defaultSurface}
                      onChange={(e) => setDefaultSurface(parseFloat(e.target.value) || 70)}
                      className="w-24 rounded-sm"
                      data-testid="default-surface-input"
                    />
                    <span className="text-sm text-muted-foreground">m² pour tous les logements</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-3">
                      Entrez la surface de chaque logement :
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {Array.from({ length: form174Data.nombre_logements }, (_, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground w-6">#{i + 1}</span>
                          <Input
                            type="number"
                            value={customSurfaces[i] || defaultSurface}
                            onChange={(e) => handleCustomSurfaceChange(i, e.target.value)}
                            className="w-20 rounded-sm text-sm"
                            data-testid={`surface-logement-${i}`}
                          />
                          <span className="text-xs text-muted-foreground">m²</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Montant devis calculé */}
              <div className="mt-4 p-4 bg-emerald-500/10 border border-green-200 rounded-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">Montant devis calculé</p>
                    <p className="text-xs text-green-600 mt-1">
                      Basé sur {form174Data.nombre_logements} logement{form174Data.nombre_logements > 1 ? "s" : ""}, 
                      scénario {form174Data.scenario_classes} classes
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-green-700" data-testid="montant-devis-174">
                    {formatCurrency(montantDevis174)}
                  </span>
                </div>
                <p className="text-xs text-green-600 mt-3">
                  Ce montant pourra être modifié manuellement dans le détail du dossier si nécessaire.
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="form-group">
              <Label htmlFor="notes_174">Notes (optionnel)</Label>
              <Textarea
                id="notes_174"
                data-testid="notes-174-input"
                value={form174Data.notes}
                onChange={(e) => setForm174Data({ ...form174Data, notes: e.target.value })}
                placeholder="Informations complémentaires sur le dossier..."
                className="rounded-sm min-h-[100px]"
              />
            </div>

            {/* Submit */}
            <div className="flex items-center gap-4 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/dossiers?tab=174`)}
                className="rounded-sm"
                data-testid="cancel-174-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading || !form174Data.organisme_bailleur || !form174Data.profile}
                className="rounded-sm shadow-none bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]"
                data-testid="submit-174-btn"
              >
                {loading ? "Création..." : "Créer le dossier 174"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Op. Cumulée Tab */}
        <TabsContent value="cumule">
          <div className="max-w-4xl">
            <div className="border border-violet-500/30 bg-violet-500/10 dark:bg-violet-900/20 rounded-xl p-8 text-center space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 dark:bg-violet-800/50 flex items-center justify-center mx-auto">
                <FileText className="w-8 h-8 text-violet-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl mb-2">Créer un dossier Op. Cumulée (174 + MPR)</h2>
                <p className="text-muted-foreground max-w-lg mx-auto">
                  Le simulateur par profil vous guidera étape par étape pour créer un dossier combinant les programmes 174 et MPR, 
                  avec le calcul automatique du CA.
                </p>
              </div>
              <Button
                onClick={() => navigate("/simulateur-profil?mode=create")}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white shadow-lg hover:shadow-xl transition-all px-8 py-3"
                data-testid="goto-simulateur-cumule-btn"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Lancer le simulateur
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
